import React, { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Film, ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '@components/Button';
import { Dialog } from '@components/Dialog';
import mediaService from '@services/mediaService';
import type { MediaBrief } from '@/types/social';
import { formatCoverTime } from '@/utils/social';
import { BriefThumb } from './SocialBits';
import { MediaPickerDialog } from './MediaPickerDialog';

export interface ReelCoverValue {
  cover_at_seconds: number | null;
  cover_media_id: number | null;
}

interface ReelCoverDialogProps {
  isOpen: boolean;
  onClose: () => void;
  video?: MediaBrief;
  value: ReelCoverValue;
  /** Brief da imagem de capa já escolhida (para a prévia). */
  coverBrief?: MediaBrief;
  /** O formato aceita imagem da Biblioteca como capa (Reels do Instagram). */
  allowImage: boolean;
  readOnly?: boolean;
  onSave: (value: ReelCoverValue, picked?: MediaBrief) => void;
}

/**
 * Capa do vídeo: um quadro do próprio vídeo ou, no Reels do Instagram, uma imagem da Biblioteca.
 * Documentação: src/docs/redes-sociais/posts.md ("Capa do vídeo").
 */
export const ReelCoverDialog: React.FC<ReelCoverDialogProps> = ({ isOpen, onClose, video, value, coverBrief, allowImage, readOnly, onSave }) => {
  const [mode, setMode] = useState<'frame' | 'image'>('frame');
  const [seconds, setSeconds] = useState(0);
  const [image, setImage] = useState<MediaBrief | undefined>();
  const [videoUrl, setVideoUrl] = useState<string>();
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [duration, setDuration] = useState(0);
  const [picker, setPicker] = useState(false);
  const ref = useRef<HTMLVideoElement>(null);

  // Reabre com o que está salvo; depende só de valores primitivos (o pai recria os objetos a cada render).
  const savedSeconds = value.cover_at_seconds;
  const savedImage = allowImage ? value.cover_media_id : null;
  const videoId = video?.id;
  const videoDuration = video?.duration_seconds;
  useEffect(() => {
    if (!isOpen) return;
    setMode(savedImage != null ? 'image' : 'frame');
    setSeconds(savedSeconds ?? 0);
    setImage(savedImage != null ? coverBrief : undefined);
    setDuration(videoDuration ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- coverBrief acompanha savedImage
  }, [isOpen, savedSeconds, savedImage, videoDuration]);

  useEffect(() => {
    if (!isOpen || !videoId) return;
    let cancelled = false;
    setLoadingVideo(true);
    setVideoUrl(undefined);
    mediaService
      .getUrl(videoId, 'original')
      .then((u) => !cancelled && setVideoUrl(u.url))
      .catch(() => undefined)
      .finally(() => !cancelled && setLoadingVideo(false));
    return () => {
      cancelled = true;
    };
  }, [isOpen, videoId]);

  const seek = (s: number) => {
    setSeconds(s);
    if (ref.current) ref.current.currentTime = s;
  };

  const save = () => {
    if (mode === 'image' && image) onSave({ cover_at_seconds: value.cover_at_seconds, cover_media_id: image.id }, image);
    else onSave({ cover_at_seconds: Math.round(seconds * 10) / 10, cover_media_id: null });
    onClose();
  };

  const tab = (key: 'frame' | 'image', label: string, icon: React.ReactNode) => (
    <button
      type="button"
      onClick={() => setMode(key)}
      className={clsx(
        'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-small font-medium',
        mode === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900',
      )}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title="Capa do vídeo"
        size="lg"
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            {!readOnly && (value.cover_at_seconds != null || value.cover_media_id != null) ? (
              <Button
                variant="outline"
                onClick={() => {
                  onSave({ cover_at_seconds: null, cover_media_id: null });
                  onClose();
                }}
              >
                Remover capa
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={onClose}>
                {readOnly ? 'Fechar' : 'Cancelar'}
              </Button>
              {!readOnly && (
                <Button onClick={save} disabled={mode === 'image' && !image}>
                  Usar esta capa
                </Button>
              )}
            </div>
          </div>
        }
      >
        <div className="space-y-3">
          {allowImage && (
            <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
              {tab('frame', 'Quadro do vídeo', <Film size={14} />)}
              {tab('image', 'Imagem da biblioteca', <ImageIcon size={14} />)}
            </div>
          )}

          {mode === 'frame' ? (
            <div className="space-y-2">
              <p className="text-small text-slate-600">
                Arraste para escolher o momento do vídeo que vira a capa. Sem escolha, a rede usa o primeiro quadro.
              </p>
              <div className="mx-auto w-full max-w-[16rem] overflow-hidden rounded-lg bg-black aspect-[9/16]">
                {videoUrl ? (
                  <video
                    ref={ref}
                    src={videoUrl}
                    poster={video?.preview_url ?? video?.thumb_url}
                    muted
                    playsInline
                    preload="auto"
                    className="h-full w-full object-cover"
                    onLoadedMetadata={(e) => {
                      const v = e.currentTarget;
                      if (Number.isFinite(v.duration)) setDuration(v.duration);
                      v.currentTime = seconds;
                    }}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-slate-400">
                    {loadingVideo ? <Loader2 className="animate-spin" /> : <BriefThumb media={video} className="h-full w-full" />}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  className="flex-1 accent-emerald-600"
                  min={0}
                  max={Math.max(duration, 0.1)}
                  step={0.1}
                  value={Math.min(seconds, duration || seconds)}
                  disabled={readOnly || !videoUrl}
                  onChange={(e) => seek(Number(e.target.value))}
                  aria-label="Momento da capa"
                />
                <span className="w-24 text-right text-small tabular-nums text-slate-700">
                  {formatCoverTime(seconds)} / {formatCoverTime(duration)}
                </span>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-small text-slate-600">
                A imagem aparece como capa no perfil, na grade e no feed. Ela é cortada em 9:16 (1080 × 1920); deixe o principal no centro,
                porque a grade do Instagram mostra só o meio da capa.
              </p>
              <div className="mx-auto w-full max-w-[16rem]">
                {image ? (
                  <BriefThumb media={image} className="w-full rounded-lg aspect-[9/16]" />
                ) : (
                  <div className="flex aspect-[9/16] w-full items-center justify-center rounded-lg border-2 border-dashed border-slate-300 text-small text-slate-500">
                    Nenhuma imagem
                  </div>
                )}
              </div>
              {!readOnly && (
                <div className="flex justify-center">
                  <Button variant="outline" size="sm" icon={<ImageIcon />} onClick={() => setPicker(true)}>
                    {image ? 'Trocar imagem' : 'Escolher da biblioteca'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </Dialog>
      {picker && (
        <MediaPickerDialog isOpen onClose={() => setPicker(false)} kinds={['image']} max={1} onPick={(picked) => picked[0] && setImage(picked[0])} />
      )}
    </>
  );
};

export default ReelCoverDialog;
